import React, { createContext, useContext, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

interface MediaPermissionState {
    audio: boolean | null;
    video: boolean | null;
}

interface MediaPermissionError {
    audioError: string | null;
    videoWarning: string | null;
}

interface MediaPermissionContextType {
    permissions: MediaPermissionState;
    errors: MediaPermissionError;
    hasRequiredPermissions: boolean;
    stream: MediaStream | null;
    requestPermissions: () => Promise<void>;
}

const MediaPermissionContext = createContext<
    MediaPermissionContextType | undefined
>(undefined);

export const MediaPermissionProvider: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const [permissions, setPermissions] = useState<MediaPermissionState>({
        audio: null,
        video: null,
    });

    const [errors, setErrors] = useState<MediaPermissionError>({
        audioError: null,
        videoWarning: null,
    });

    const [stream, setStream] = useState<MediaStream | null>(null);
    const location = useLocation();

    const requestPermissions = async () => {
        try {
            // First try to get both permissions
            const mediaStream = await navigator.mediaDevices.getUserMedia({
                audio: true,
                video: true,
            });

            setPermissions({
                audio: true,
                video: true,
            });

            // Store the stream instead of stopping it
            setStream(mediaStream);
            setErrors({
                audioError: null,
                videoWarning: null,
            });
        } catch (err) {
            // If getting both fails, try audio only
            try {
                const audioStream = await navigator.mediaDevices.getUserMedia({
                    audio: true,
                });

                setPermissions((prev) => ({
                    ...prev,
                    audio: true,
                    video: false,
                }));

                setErrors((prev) => ({
                    ...prev,
                    videoWarning: "Video access not available or denied",
                }));

                // Store the audio stream
                setStream(audioStream);
            } catch (audioErr) {
                // Audio permission denied - this is a critical error
                setPermissions((prev) => ({
                    ...prev,
                    audio: false,
                    video: false,
                }));
                setErrors((prev) => ({
                    ...prev,
                    audioError:
                        "Microphone access denied - this is required for the application",
                }));
                setStream(null);
            }
        }
    };

    // Check permissions on mount
    useEffect(() => {
        // Only request permissions for the mock-case or report pages
        if (
            location.pathname.includes("/mock-case") ||
            location.pathname.includes("/case-mock")
        ) {
            requestPermissions();
        } else {
            // For all other routes, disable and cleanup any audio/video access
            setPermissions({
                audio: null,
                video: null,
            });
            setErrors({
                audioError: null,
                videoWarning: null,
            });
            if (stream) {
                stream.getTracks().forEach((track) => track.stop());
            }
            setStream(null);
        }

        // Cleanup when unmounting
        return () => {
            if (stream) {
                stream.getTracks().forEach((track) => track.stop());
            }
        };
    }, [location.pathname]);

    const hasRequiredPermissions = permissions.audio === true;

    return (
        <MediaPermissionContext.Provider
            value={{
                permissions,
                errors,
                hasRequiredPermissions,
                stream,
                requestPermissions,
            }}>
            {children}
        </MediaPermissionContext.Provider>
    );
};

export const useMediaPermission = () => {
    const context = useContext(MediaPermissionContext);
    if (context === undefined) {
        throw new Error(
            "useMediaPermission must be used within a MediaPermissionProvider"
        );
    }
    return context;
};
